/**
 * API Configuration
 * Centralized configuration for all API endpoints and settings
 */

// Base URL - should be moved to environment variables in production
const API_BASE_URL: string = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_VERSION: string = '/api/v1';

/**
 * API Endpoints Configuration Types
 */
interface AuthEndpoints {
  LOGIN: string;
  LOGOUT: string;
  REFRESH: string;
}

interface ChatEndpoints {
  MESSAGE: string;
}

interface FeedbackEndpoints {
  CREATE: string;
}

interface DocumentEndpoints {
  UPLOAD_ASYNC: string;
  STATUS: (jobId: string) => string;
  RESULT: (jobId: string) => string;
  PROGRESS_SSE: (jobId: string) => string;
  CANCEL: (jobId: string) => string;
  JOBS: string;
  HISTORY: (userId: string) => string;
  RESULTS: (userId: string) => string;
}

interface HtsReasoningEndpoints {
  REASONING_SSE: (jobId: string) => string;
  QUESTIONS: (jobId: string) => string;
  ANSWERS: (jobId: string) => string;
}

interface ReclassificationEndpoints {
  RECLASSIFY: (documentId: string) => string;
  HISTORY: (documentId: string) => string;
}

interface ValidationEndpoints {
  CLASSIFY: string;
  HTS: string;
}

interface VerificationEndpoints {
  STATUS: (sessionId: string) => string;
  RESPOND: string;
}

interface ApiEndpoints {
  BASE_URL: string;
  API_VERSION: string;
  BASE: string;
  HEALTH: string;
  HEALTH_DETAILED: string;
  AUTH: AuthEndpoints;
  CHAT: ChatEndpoints;
  DOCUMENTS: DocumentEndpoints;
  VALIDATION: ValidationEndpoints;
  VERIFICATION: VerificationEndpoints;
  FEEDBACK: FeedbackEndpoints;
  RECLASSIFICATION: ReclassificationEndpoints;
  HTS_REASONING: HtsReasoningEndpoints;
}

/**
 * API Endpoints
 */
export const API_ENDPOINTS: ApiEndpoints = {
  // Base
  BASE_URL: API_BASE_URL,
  API_VERSION: API_VERSION,
  BASE: `${API_BASE_URL}${API_VERSION}`,

  // Health
  HEALTH: '/health',
  HEALTH_DETAILED: '/health/detailed',

  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
  },

  // Chat
  CHAT: {
    MESSAGE: '/chat/message',
  },

  // Document Processing - Async
  DOCUMENTS: {
    UPLOAD_ASYNC: '/documents/upload-async',
    STATUS: (jobId: string): string => `/documents/status/${jobId}`,
    RESULT: (jobId: string): string => `/documents/result/${jobId}`,
    PROGRESS_SSE: (jobId: string): string => `/documents/progress/${jobId}`,
    CANCEL: (jobId: string): string => `/documents/cancel/${jobId}`,
    JOBS: '/documents/jobs',
    HISTORY: (userId: string): string => `/documents/user/${userId}/history`,
    RESULTS: (userId: string): string => `/documents/user/${userId}/results`,
  },

  // Validation
  VALIDATION: {
    CLASSIFY: '/validation/classify',
    HTS: '/validation/hts',
  },

  // Verification
  VERIFICATION: {
    STATUS: (sessionId: string): string => `/verification/status/${sessionId}`,
    RESPOND: '/verification/respond',
  },

  // Feedback
  FEEDBACK: {
    CREATE: '/feedback',
  },

  // Reclassification
  RECLASSIFICATION: {
    RECLASSIFY: (documentId: string): string => `/reclassify/document/${documentId}`,
    HISTORY: (documentId: string): string => `/reclassify/document/${documentId}/history`,
  },

  // HTS Reasoning (iterative agent)
  HTS_REASONING: {
    REASONING_SSE: (jobId: string): string => `/hts/reasoning/${jobId}`,
    QUESTIONS: (jobId: string): string => `/hts/questions/${jobId}`,
    ANSWERS: (jobId: string): string => `/hts/answers/${jobId}`,
  },
};

/**
 * File Upload Configuration Types
 */
interface FileUploadConfig {
  MAX_SIZE: number;
  ACCEPTED_TYPES: string[];
  ACCEPTED_MIME_TYPES: string[];
}

interface PollingConfig {
  INTERVAL: number;
  MAX_ATTEMPTS: number;
  REQUEST_TIMEOUT: number;
  RETRY_DELAY: number;
}

interface RetryConfig {
  MAX_RETRIES: number;
  RETRY_DELAY: number;
  BACKOFF_MULTIPLIER: number;
}

interface VerificationConfig {
  POLLING_INTERVAL: number;
  MAX_WAIT_TIME: number;
}

interface ApiConfig {
  TIMEOUT: number;
  UPLOAD_TIMEOUT: number;
  POLLING: PollingConfig;
  FILE_UPLOAD: FileUploadConfig;
  RETRY: RetryConfig;
  VERIFICATION: VerificationConfig;
}

/**
 * API Configuration Settings
 */
export const API_CONFIG: ApiConfig = {
  // Timeout settings (in milliseconds)
  TIMEOUT: 90000, // 1 minute 30 seconds
  UPLOAD_TIMEOUT: 120000, // 2 minutes for file uploads

  // Polling settings
  POLLING: {
    INTERVAL: 3000, // Poll every 3 seconds
    MAX_ATTEMPTS: 100, // Maximum polling attempts
    REQUEST_TIMEOUT: 3000, // 3 second timeout for each polling request
    RETRY_DELAY: 1000, // Wait 1 second before retrying after timeout
  },

  // File upload settings
  FILE_UPLOAD: {
    MAX_SIZE: 10 * 1024 * 1024, // 10MB
    ACCEPTED_TYPES: ['.pdf', '.png', '.jpg', '.jpeg', '.xml'],
    ACCEPTED_MIME_TYPES: [
      'application/pdf',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'application/xml',
      'text/xml',
    ],
  },

  // Retry configuration
  RETRY: {
    MAX_RETRIES: 3,
    RETRY_DELAY: 1000, // 1 second
    BACKOFF_MULTIPLIER: 2, // Exponential backoff
  },

  // Verification settings
  VERIFICATION: {
    POLLING_INTERVAL: 1000, // Poll every 1 second for verification
    MAX_WAIT_TIME: 300000, // 5 minutes max wait for user response
  },
};

/**
 * HTTP Status Codes Types
 */
interface HttpStatus {
  OK: number;
  CREATED: number;
  BAD_REQUEST: number;
  UNAUTHORIZED: number;
  FORBIDDEN: number;
  NOT_FOUND: number;
  CONFLICT: number;
  PAYLOAD_TOO_LARGE: number;
  INTERNAL_SERVER_ERROR: number;
  SERVICE_UNAVAILABLE: number;
}

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS: HttpStatus = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  PAYLOAD_TOO_LARGE: 413,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

export default API_ENDPOINTS;
